import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex ga-2" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_4 = { class: "text-h5" }
const _hoisted_5 = { class: "text-subtitle-2 mb-2" }
const _hoisted_6 = { class: "text-caption" }
const _hoisted_7 = {
  key: 0,
  class: "text-grey mt-1"
}

import { ref, onMounted, computed } from 'vue'
import { api } from '@/composables/api'
import type { School } from '@/types/School'


export default /*@__PURE__*/_defineComponent({
  __name: 'SchoolsTable',
  setup(__props) {

const schools = ref<School[]>([])
const loading = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)
const submitting = ref(false)
const users = ref<any[]>([])
const searchQuery = ref('')

// Bulk add state
const showBulkModal = ref(false)
const submittingBulk = ref(false)
const bulkFormData = ref({
  county: '',
  schoolsList: ''
})

const headers = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'County', key: 'county', sortable: true },
  { title: 'Point of Contact', key: 'poc', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false }
]

const formData = ref({
  name: '',
  county: '',
  poc: null as string | null
})

const userOptions = computed(() => {
  return users.value.map(user => ({
    title: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
    value: user.id
  }))
})

const getUserName = (userId: string) => {
  const user = users.value.find(u => u.id === userId)
  if (!user) return 'Unknown'
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
}

// Filtered schools based on search query
const filteredSchools = computed(() => {
  if (!searchQuery.value) return schools.value

  const query = searchQuery.value.toLowerCase()
  return schools.value.filter(school =>
    school.name.toLowerCase().includes(query) ||
    (school.county && school.county.toLowerCase().includes(query))
  )
})

// Bulk add preview - parse the schools list
const bulkPreview = computed(() => {
  if (!bulkFormData.value.schoolsList) return []
  return bulkFormData.value.schoolsList
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
})

const loadSchools = async () => {
  loading.value = true
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    alert('Failed to load schools')
    schools.value = []
  } finally {
    loading.value = false
  }
}

const loadUsers = async () => {
  try {
    const response = await api.get('/users')
    users.value = response.data || response || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const openAddModal = () => {
  isEditing.value = false
  editingId.value = null
  formData.value = {
    name: '',
    county: '',
    poc: null
  }
  showModal.value = true
}

const openEditModal = (school: any) => {
  isEditing.value = true
  editingId.value = school.id
  formData.value = {
    name: school.name,
    county: school.county,
    poc: school.poc || null
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  isEditing.value = false
  editingId.value = null
  formData.value = {
    name: '',
    county: '',
    poc: null
  }
}

const saveSchool = async () => {
  if (!formData.value.name || !formData.value.county) {
    alert('Please fill in all required fields')
    return
  }

  submitting.value = true
  try {
    if (isEditing.value && editingId.value) {
      await api.put(`/schools/${editingId.value}`, formData.value)
    } else {
      await api.post('/schools', formData.value)
    }

    await loadSchools()
    closeModal()
  } catch (error) {
    console.error('Error saving school:', error)
    alert('Failed to save school')
  } finally {
    submitting.value = false
  }
}

const deleteSchool = async (school: any) => {
  if (!confirm(`Are you sure you want to delete ${school.name}?`)) {
    return
  }

  try {
    await api.delete(`/schools/${school.id}`)
    await loadSchools()
  } catch (error) {
    console.error('Error deleting school:', error)
    alert('Failed to delete school')
  }
}

// Bulk add functions
const openBulkAddModal = () => {
  bulkFormData.value = {
    county: '',
    schoolsList: ''
  }
  showBulkModal.value = true
}

const closeBulkModal = () => {
  showBulkModal.value = false
  bulkFormData.value = {
    county: '',
    schoolsList: ''
  }
}

const bulkCreateSchools = async () => {
  if (!bulkFormData.value.county || !bulkFormData.value.schoolsList) {
    alert('Please fill in all required fields')
    return
  }

  const schoolNames = bulkPreview.value
  if (schoolNames.length === 0) {
    alert('No valid school names found')
    return
  }

  submittingBulk.value = true
  try {
    // Create an array of school objects
    const schoolsToCreate = schoolNames.map(name => ({
      name,
      county: bulkFormData.value.county,
      poc: null
    }))

    // Send them all in parallel
    const promises = schoolsToCreate.map(school =>
      api.post('/schools', school)
    )

    await Promise.all(promises)

    await loadSchools()
    closeBulkModal()
    alert(`Successfully created ${schoolNames.length} school(s)`)
  } catch (error) {
    console.error('Error bulk creating schools:', error)
    alert('Failed to create some or all schools. Check console for details.')
  } finally {
    submittingBulk.value = false
  }
}

onMounted(() => {
  loadSchools()
  loadUsers()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_alert = _resolveComponent("v-alert")!

  return (_openBlock(), _createBlock(_component_v_card, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center" }, {
        default: _withCtx(() => [
          _cache[12] || (_cache[12] = _createElementVNode("span", null, "Schools Management", -1)),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_btn, {
              color: "secondary",
              variant: "tonal",
              onClick: openBulkAddModal
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                    _createTextVNode("mdi-file-upload", -1)
                  ]))]),
                  _: 1
                }),
                _cache[9] || (_cache[9] = _createTextVNode(" Bulk Add Schools ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              color: "primary",
              onClick: openAddModal
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("mdi-plus", -1)
                  ]))]),
                  _: 1
                }),
                _cache[11] || (_cache[11] = _createTextVNode(" Add School ", -1))
              ]),
              _: 1
            })
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_v_card_text, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_text_field, {
            modelValue: searchQuery.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
            label: "Search schools...",
            "prepend-inner-icon": "mdi-magnify",
            variant: "outlined",
            density: "compact",
            clearable: "",
            class: "mb-4",
            "hide-details": ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_data_table, {
            headers: headers,
            items: filteredSchools.value,
            loading: loading.value,
            "items-per-page": 10,
            class: "elevation-1"
          }, {
            "item.county": _withCtx(({ item }) => [
              _createVNode(_component_v_chip, {
                color: "secondary",
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString((item as any).county), 1)
                ]),
                _: 2
              }, 1024)
            ]),
            "item.poc": _withCtx(({ item }) => [
              ((item as any).poc)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(getUserName((item as any).poc)), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_3, "-"))
            ]),
            "item.actions": _withCtx(({ item }) => [
              _createVNode(_component_v_btn, {
                icon: "mdi-pencil",
                size: "small",
                variant: "text",
                onClick: ($event: any) => (openEditModal(item))
              }, null, 8, ["onClick"]),
              _createVNode(_component_v_btn, {
                icon: "mdi-delete",
                size: "small",
                variant: "text",
                color: "error",
                onClick: ($event: any) => (deleteSchool(item))
              }, null, 8, ["onClick"])
            ]),
            _: 1
          }, 8, ["items", "loading"])
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showModal.value,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showModal).value = $event)),
        "max-width": "600px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(isEditing.value ? 'Edit School' : 'Add School'), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_form, { ref: "form" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: formData.value.name,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.name) = $event)),
                        label: "School Name",
                        variant: "outlined",
                        rules: [v => !!v || 'School name is required'],
                        required: ""
                      }, null, 8, ["modelValue", "rules"]),
                      _createVNode(_component_v_text_field, {
                        modelValue: formData.value.county,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.county) = $event)),
                        label: "County",
                        variant: "outlined",
                        rules: [v => !!v || 'County is required'],
                        required: ""
                      }, null, 8, ["modelValue", "rules"]),
                      _createVNode(_component_v_select, {
                        modelValue: formData.value.poc,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.poc) = $event)),
                        items: userOptions.value,
                        label: "Point of Contact (Optional)",
                        variant: "outlined",
                        clearable: ""
                      }, null, 8, ["modelValue", "items"])
                    ]),
                    _: 1
                  }, 512)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeModal
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: saveSchool,
                    loading: submitting.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(isEditing.value ? 'Update' : 'Create'), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showBulkModal.value,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showBulkModal).value = $event)),
        "max-width": "700px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createElementVNode("span", { class: "text-h5" }, "Bulk Add Schools", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_form, { ref: "bulkForm" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: bulkFormData.value.county,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((bulkFormData.value.county) = $event)),
                        label: "County",
                        variant: "outlined",
                        rules: [v => !!v || 'County is required'],
                        required: "",
                        class: "mb-4",
                        hint: "All schools in the list will be assigned to this county",
                        "persistent-hint": ""
                      }, null, 8, ["modelValue", "rules"]),
                      _createVNode(_component_v_textarea, {
                        modelValue: bulkFormData.value.schoolsList,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((bulkFormData.value.schoolsList) = $event)),
                        label: "Schools List",
                        variant: "outlined",
                        rules: [v => !!v || 'Schools list is required'],
                        required: "",
                        rows: "10",
                        hint: "Enter one school name per line",
                        "persistent-hint": "",
                        placeholder: "Example:\nSchool Name 1\nSchool Name 2\nSchool Name 3"
                      }, null, 8, ["modelValue", "rules"]),
                      (bulkPreview.value.length > 0)
                        ? (_openBlock(), _createBlock(_component_v_alert, {
                            key: 0,
                            type: "info",
                            variant: "tonal",
                            class: "mt-4"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_5, "Preview: " + _toDisplayString(bulkPreview.value.length) + " school(s) will be created", 1),
                              _createElementVNode("div", _hoisted_6, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bulkPreview.value.slice(0, 5), (school, index) => {
                                  return (_openBlock(), _createElementBlock("div", { key: index }, " • " + _toDisplayString(school), 1))
                                }), 128)),
                                (bulkPreview.value.length > 5)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, " ... and " + _toDisplayString(bulkPreview.value.length - 5) + " more ", 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 512)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    onClick: closeBulkModal
                  }, {
                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: bulkCreateSchools,
                    loading: submittingBulk.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Create " + _toDisplayString(bulkPreview.value.length) + " School(s) ", 1)
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})